import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  useTheme
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  TrendingUp,
  AttachMoney,
  Assessment,
  Refresh
} from '@mui/icons-material';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { getRMBProfitAnalyticsSummary, getRMBProfitAnalyticsTransactions, getRMBProfitAnalyticsCharts } from '../../services/api';
import CustomPagination from '../../components/common/CustomPagination';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const RMBProfitAnalytics = () => {
  const { t } = useTranslation(['rmbProfitAnalytics', 'common']);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [charts, setCharts] = useState(null);
  const [filters, setFilters] = useState({
    start_date: format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
    status: '',
    transaction_type: '',
    min_profit: '',
    max_profit: ''
  });
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 20,
    totalCount: 0
  });
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  const totalPages = Math.ceil(pagination.totalCount / pagination.rowsPerPage) || 1;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...filters,
        page: pagination.page + 1, // Backend expects 1-based
        page_size: pagination.rowsPerPage
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const [summaryData, transactionsData, chartsData] = await Promise.all([
        getRMBProfitAnalyticsSummary(params),
        getRMBProfitAnalyticsTransactions(params),
        getRMBProfitAnalyticsCharts(params)
      ]);

      setSummary(summaryData);
      // DRF pagination returns {results: [...], count: ..., next: ..., previous: ...}
      const transactionsList = Array.isArray(transactionsData.results) ? transactionsData.results : [];
      setTransactions(transactionsList);
      setCharts(chartsData);
      setPagination(prev => ({
        ...prev,
        totalCount: transactionsData.count || transactionsList.length || 0
      }));
    } catch (err) {
      setError(err.response?.data?.message || t('messages.error'));
      console.error('Error fetching RMB profit analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.rowsPerPage, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(pagination.rowsPerPage.toString());
    } else {
      setPagination(prev => ({ ...prev, rowsPerPage: Number(value), page: 0 }));
      setCustomRowsActive(false);
    }
  };

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setPagination(prev => ({ ...prev, rowsPerPage: val, page: 0 }));
    }
    setCustomRowsActive(false);
  };

  const formatCurrency = (amount, currency = 'RMB') => {
    if (amount === undefined || amount === null) return '0.00';
    if (currency === 'RMB') {
      return `¥${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else {
      return `₦${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };

  const getTransactionTypeLabel = (type) => {
    return t(`transactionType.${type}`) || type;
  };

  const getStatusLabel = (status) => {
    return t(`status.${status}`) || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'pending': '#FF9800',
      'processing': '#2196F3',
      'completed': '#4CAF50',
      'failed': '#F44336',
      'cancelled': '#9E9E9E'
    };
    return colorMap[status] || '#9E9E9E';
  };

  // Chart data preparation
  const profitTrendData = charts?.profit_trend ? {
    labels: charts.profit_trend.map(item => format(new Date(item.date), 'MMM dd')),
    datasets: [
      {
        label: 'Profit (RMB)',
        data: charts.profit_trend.map(item => item.total_profit_rmb),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  } : null;

  const profitByTypeData = charts?.profit_by_type ? {
    labels: charts.profit_by_type.map(item => item.type_label),
    datasets: [
      {
        label: 'Profit (RMB)',
        data: charts.profit_by_type.map(item => item.total_profit_rmb),
        backgroundColor: ['#2196F3', '#4CAF50']
      }
    ]
  } : null;

  // DataGrid columns
  const columns = useMemo(() => [
    {
      field: 'sn',
      headerName: t('table.sn'),
      width: 80,
      sortable: false,
      filterable: false
    },
    {
      field: 'transaction_id',
      headerName: t('table.transactionId'),
      flex: 1,
      minWidth: 150,
      sortable: false
    },
    {
      field: 'created_at',
      headerName: t('table.date'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        try {
          return format(new Date(params.value), 'MMM dd, yyyy');
        } catch {
          return 'N/A';
        }
      }
    },
    {
      field: 'user',
      headerName: t('table.customer'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => params.row.user?.username || 'N/A'
    },
    {
      field: 'transaction_type',
      headerName: t('table.transactionType'),
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={getTransactionTypeLabel(params.value)}
          size="small"
          color="primary"
          variant="outlined"
        />
      )
    },
    {
      field: 'rmb_amount',
      headerName: t('table.rmbAmount'),
      width: 140,
      sortable: false,
      renderCell: (params) => formatCurrency(params.value)
    },
    {
      field: 'ngn_amount',
      headerName: t('table.ngnAmount'),
      width: 140,
      sortable: false,
      renderCell: (params) => formatCurrency(params.value, 'NGN')
    },
    {
      field: 'exchange_rate',
      headerName: t('table.userRate'),
      width: 120,
      sortable: false,
      renderCell: (params) => params.value || 'N/A'
    },
    {
      field: 'actual_rate_used',
      headerName: t('table.actualRate'),
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const value = params.value;
        if (value === null || value === undefined || value === '') return '0.00';
        return parseFloat(value).toFixed(2);
      }
    },
    {
      field: 'profit_rmb',
      headerName: t('table.profitRmb'),
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Typography sx={{ color: '#10b981', fontWeight: 'bold' }}>
          {formatCurrency(params.value)}
        </Typography>
      )
    },
    {
      field: 'profit_naira',
      headerName: t('table.profitNaira'),
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Typography sx={{ color: '#10b981', fontWeight: 'bold' }}>
          {formatCurrency(params.value, 'NGN')}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: t('table.status'),
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={getStatusLabel(params.value)}
          size="small"
          sx={{
            backgroundColor: getStatusColor(params.value),
            color: 'white',
            fontWeight: 'bold'
          }}
        />
      )
    }
  ], [t]);

  // Add serial numbers to transactions
  const rowsWithSerial = useMemo(() => {
    return transactions.map((transaction, idx) => ({
      ...transaction,
      id: transaction.sid || transaction.id || idx,
      sn: (pagination.page * pagination.rowsPerPage) + idx + 1
    }));
  }, [transactions, pagination.page, pagination.rowsPerPage]);

  if (loading && !summary) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 2 }}>
          💰 {t('title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading}
          >
            {t('common:buttons.refresh')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label={t('filters.startDate')}
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label={t('filters.endDate')}
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('filters.transactionType')}</InputLabel>
                <Select
                  value={filters.transaction_type}
                  label={t('filters.transactionType')}
                  onChange={(e) => handleFilterChange('transaction_type', e.target.value)}
                >
                  <MenuItem value="">{t('filters.all')}</MenuItem>
                  <MenuItem value="alipay">{t('transactionType.alipay')}</MenuItem>
                  <MenuItem value="bank">{t('transactionType.bank')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                label={t('filters.minProfit')}
                type="number"
                value={filters.min_profit}
                onChange={(e) => handleFilterChange('min_profit', e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                label={t('filters.maxProfit')}
                type="number"
                value={filters.max_profit}
                onChange={(e) => handleFilterChange('max_profit', e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ backgroundColor: '#f0f9ff', border: '2px solid #0ea5e9' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('summary.totalProfitRmb')}
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#0ea5e9', fontWeight: 'bold' }}>
                      {formatCurrency(summary.total_profit_rmb)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency(summary.total_profit_naira, 'NGN')}
                    </Typography>
                  </Box>
                  <AttachMoney sx={{ fontSize: 40, color: '#0ea5e9' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ backgroundColor: '#f0fdf4', border: '2px solid #10b981' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('summary.totalTransactions')}
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#10b981', fontWeight: 'bold' }}>
                      {summary.total_transactions || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {summary.profitable_transactions} {t('summary.profitableTransactions')}
                    </Typography>
                  </Box>
                  <Assessment sx={{ fontSize: 40, color: '#10b981' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ backgroundColor: '#fef3c7', border: '2px solid #f59e0b' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('summary.avgProfitPerTransaction')}
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#f59e0b', fontWeight: 'bold' }}>
                      {formatCurrency(summary.average_profit_per_transaction_rmb)}
                    </Typography>
                  </Box>
                  <TrendingUp sx={{ fontSize: 40, color: '#f59e0b' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ backgroundColor: '#f3e8ff', border: '2px solid #8b5cf6' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('summary.totalRmbAmount')}
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#8b5cf6', fontWeight: 'bold' }}>
                      {formatCurrency(summary.total_rmb_amount)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency(summary.total_ngn_amount, 'NGN')}
                    </Typography>
                  </Box>
                  <AttachMoney sx={{ fontSize: 40, color: '#8b5cf6' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Charts */}
      {charts && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('charts.profitTrend')}
                </Typography>
                {profitTrendData && (
                  <Box sx={{ height: 300 }}>
                    <Line data={profitTrendData} options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: true },
                        tooltip: { mode: 'index', intersect: false }
                      },
                      scales: {
                        y: { beginAtZero: true }
                      }
                    }} />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('charts.profitByType')}
                </Typography>
                {profitByTypeData && (
                  <Box sx={{ height: 300 }}>
                    <Bar data={profitByTypeData} options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false }
                      },
                      scales: {
                        y: { beginAtZero: true }
                      }
                    }} />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Exchange Rate Comparison */}
          {charts.rate_comparison && (
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('charts.exchangeRateComparison')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                    <Typography variant="body1">
                      <strong>{t('charts.actualRate')}:</strong> {charts.rate_comparison.actual_rate || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('charts.mostCommonRates')}:
                    </Typography>
                    {charts.rate_comparison.user_rates?.slice(0, 5).map((rate, idx) => (
                      <Chip
                        key={idx}
                        label={`${rate.rate} (${rate.transaction_count} transactions)`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Transactions Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520
          }}>
            <DataGrid
              rows={rowsWithSerial}
              columns={columns}
              disableSelectionOnClick
              loading={loading}
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                minWidth: 1200,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-row': {
                  marginBottom: '4px',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Pagination */}
      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={pagination.page}
          totalPages={totalPages}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>
    </Box>
  );
};

export default RMBProfitAnalytics;

